import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../utils/config';

/**
 * Custom hook for fetching profile data (education, experience, skills)
 * @returns {Object} Profile data and loading states
 */
const useProfileData = () => {
    const [data, setData] = useState({
        educations: [],
        experiences: [],
        skills: []
    });
    
    const [loading, setLoading] = useState({
        educations: true,
        experiences: true,
        skills: true
    });
    
    const [errors, setErrors] = useState({
        educations: null,
        experiences: null,
        skills: null
    });

    const fetchData = useCallback(async () => {
        const headers = { 'Content-Type': 'application/json; charset=UTF-8' };
        
        // Reset loading states
        setLoading({
            educations: true,
            experiences: true,
            skills: true
        });

        // Reset errors
        setErrors({
            educations: null,
            experiences: null,
            skills: null
        });

        const endpoints = [
            { key: 'educations', url: API_ENDPOINTS.EDUCATION_ENDPOINT },
            { key: 'experiences', url: API_ENDPOINTS.EXPERIENCES_ENDPOINT },
            { key: 'skills', url: API_ENDPOINTS.SKILL_ENDPOINT }
        ];

        // Fetch data independently to allow partial loading
        endpoints.forEach(async ({ key, url }) => {
            try {
                const response = await axios.get(url, { headers });
                setData(prev => ({ ...prev, [key]: response.data }));
                setLoading(prev => ({ ...prev, [key]: false }));
            } catch (error) {
                console.error(`Error fetching ${key}:`, error);
                setErrors(prev => ({ ...prev, [key]: error.message }));
                setLoading(prev => ({ ...prev, [key]: false }));
            }
        });
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const isAnyLoading = Object.values(loading).some(Boolean);
    const hasAnyError = Object.values(errors).some(Boolean);

    return {
        data,
        loading,
        errors,
        isAnyLoading,
        hasAnyError,
        refetch: fetchData
    };
};

export default useProfileData;